import {
  ExpressionVariables,
  defaultVariables,
  pickVersionStoreySumValues,
  toGetStoreySummary,
} from '@shared/helpers/expression-variables.helpers';
import {
  DesignExpressionVariables,
  emptyStoreySummaryVariables,
  type StoreyExpressionVariables,
} from '@shared/models/design/design-expression-variables.model';
import { getKeys, omit } from '@shared/helpers/object.helpers';
import {
  estimateRectangleDimensions,
  getLargestDesignActivity,
  getStoreySumFromDesignExpressionVariables,
  getStoreysCountFromStacks,
  resolveStoreySelectors,
} from './storey-sum-from-design.helpers';
import { ActivityType } from '@shared/models/design/activities/activity-type.model';
import { ACTIVITY_EXPRESSION_VARIABLES } from '@shared/constants/activities.constants';
import type { ActivityExpressionVariables } from '@shared/models/activities.model';
import { getHeatedArea } from '@shared/helpers/energy/energy.helpers';
import { EXPRESSION_VARIABLE_NAME as NAME } from '@shared/constants/expression-variables-names.constant';
import { CALCULATIONS } from '@shared/constants/calculations.constants';
import { expressionVariablesConstants } from '@shared/constants/expression-variables.constants';
import { getBelowGroundFromDesignStoreys } from '../migration/design-migrations.helpers';
import { createIsActivityFn } from './design-expression-functions.helpers';
import type { StoreySelector } from '@shared/helpers/storeys.helpers';
import { toInternalGetStoreySelectors } from './get-storey-index.helpers';
/** Explicit keys of ExpressionVariables (excludes index signature key). */
type ExpressionVariablesKey = Exclude<keyof ExpressionVariables, string>;

/**
 * Merge type: ExpressionVariables with DesignExpressionVariables overlaid.
 * Map only over explicit EV keys so we avoid inheriting EV's index signature;
 * then add an index signature that accepts both EV and design value types
 * so the spread merge is assignable without casting.
 */
export type DesignToProjectExpressionVariablesMigrationResult = {
  [K in ExpressionVariablesKey]: K extends keyof DesignExpressionVariables
    ? DesignExpressionVariables[K]
    : ExpressionVariables[K];
} & Pick<
  DesignExpressionVariables,
  Exclude<keyof DesignExpressionVariables, keyof ExpressionVariables>
> & {
    [key: string]:
      | ExpressionVariables[keyof ExpressionVariables]
      | DesignExpressionVariables[keyof DesignExpressionVariables]
      | undefined;
  };

/**
 * Storey stacks used for site-wide rollups. Falls back to first-building
 * {@link DesignExpressionVariables.storeys} when per-building stacks are absent.
 */
const getStoreyStacksFromDesignVariables = (
  designVariables: DesignExpressionVariables,
): StoreyExpressionVariables[][] =>
  designVariables.storeysByBuilding ?? [designVariables.storeys];

/**
 * Largest storey GFA on a stack — same best-effort footprint as the single-building path.
 */
const getBuildingFootprintFromStoreys = (
  storeys: StoreyExpressionVariables[],
): number => Math.max(0, ...storeys.map((storey) => storey.gfa), 0);

/**
 * Site footprint is the sum of each building's footprint. Width is the widest
 * per-building rectangle so area and perimeter stay on the same stack.
 */
const getBuildingFootprintAndWidthFromDesignVariables = (
  designVariables: DesignExpressionVariables,
): { building_footprint: number; building_width: number } => {
  const stacks = getStoreyStacksFromDesignVariables(designVariables);
  const footprints = stacks.map(getBuildingFootprintFromStoreys);
  const building_footprint = footprints.reduce((sum, area) => sum + area, 0);
  const widths = stacks.map((storeys, index) => {
    const perimeter = Math.max(
      0,
      ...storeys.map((storey) => storey.perimeter),
      0,
    );
    return estimateRectangleDimensions({
      area: footprints[index] ?? 0,
      perimeter,
    }).width;
  });
  return {
    building_footprint,
    building_width: Math.max(0, ...widths, 0),
  };
};

/**
 * Deepest below-ground depth across every building stack.
 */
const getBelowGroundFromDesignVariables = (
  designVariables: DesignExpressionVariables,
): number =>
  Math.max(
    0,
    ...getStoreyStacksFromDesignVariables(designVariables).map(
      getBelowGroundFromDesignStoreys,
    ),
    0,
  );

/**
 * Pick legacy activity expression variables from {@link DesignExpressionVariables}.
 * This intentionally only maps keys used by legacy expressions (Henrik prefers stable key names).
 * @param designVariables Calculated design expression variables.
 * @returns Legacy activity expression variables surface.
 */
export const getProjectActivityExpressionVariablesFromDesign = (
  designVariables: DesignExpressionVariables,
): ActivityExpressionVariables => {
  const residential = designVariables.programs[ActivityType.Residential];
  const stairwells = designVariables.programs[ActivityType.Stairwells];

  return {
    apartments_living_area_per_apartment:
      residential?.apartments_living_area_per_apartment ??
      ACTIVITY_EXPRESSION_VARIABLES.apartments_living_area_per_apartment,
    apartments_balcony_area_per_apartment:
      residential?.apartments_balcony_area_per_apartment ?? 0,
    apartments_bathroom_area_per_apartment:
      residential?.apartments_bathroom_area_per_apartment ?? 0,

    stairwells_elevators_per_stairwell:
      stairwells?.stairwells_elevators_per_stairwell ?? 0,
    stairwells_apartments_per_stairwell_per_storey:
      stairwells?.stairwells_apartments_per_stairwell_per_storey ?? 0,

    // These activity types don't exist in the design tool yet; keep legacy defaults.
    laundry_room_gfa: 0,
    bike_room_gfa: 0,

    storage_gfa: designVariables.programs[ActivityType.Storage]?.gfa ?? 0,
    building_utilities_gfa:
      designVariables.programs[ActivityType.Utilities]?.gfa ?? 0,
    garage_gfa: designVariables.programs[ActivityType.Garage]?.gfa ?? 0,
    gfa_other_activities:
      designVariables.programs[ActivityType.Other]?.gfa ?? 0,
  };
};

/**
 * Same surface as {@link getProjectExpressionVariables}, derived from design variables.
 */
export const getProjectExpressionVariablesFromDesignExpressionVariables = (
  designVariables: DesignExpressionVariables,
): ExpressionVariables => {
  const { activityId, activityType: largestActivityType } =
    getLargestDesignActivity(designVariables);
  const storeySum = getStoreySumFromDesignExpressionVariables(designVariables);

  const getStorey = (...selectors: StoreySelector[]) => {
    const internalSelectors = toInternalGetStoreySelectors(
      resolveStoreySelectors(selectors),
    );
    return toGetStoreySummary(
      internalSelectors.length === 0
        ? storeySum
        : getStoreySumFromDesignExpressionVariables(
            designVariables,
            internalSelectors,
          ),
    );
  };

  const activityExpressionValues =
    getProjectActivityExpressionVariablesFromDesign(designVariables);

  const {
    apartments_living_area_per_apartment,
    apartments_balcony_area_per_apartment,
    apartments_bathroom_area_per_apartment,
    stairwells_apartments_per_stairwell_per_storey,
    stairwells_elevators_per_stairwell,
    laundry_room_gfa,
    bike_room_gfa,
    storage_gfa,
    building_utilities_gfa,
    garage_gfa,
  } = activityExpressionValues;

  const gfa = designVariables.gfa;
  const below_ground = getBelowGroundFromDesignVariables(designVariables);
  const atemp = getHeatedArea(gfa);

  const { building_footprint, building_width } =
    getBuildingFootprintAndWidthFromDesignVariables(designVariables);

  const gfa_elevator_per_stairwell_per_level =
    stairwells_elevators_per_stairwell *
    expressionVariablesConstants.gfa_per_elevator_per_level;

  const piling_count =
    expressionVariablesConstants.piling_count_per_building_gfa * gfa;

  const activity_electricity = CALCULATIONS.activity_electricity.calculate({
    occupant_count: storeySum.occupant_count,
    activity: activityId,
  });

  return {
    ...defaultVariables,
    ...pickVersionStoreySumValues(storeySum),
    below_ground,
    activity: activityId,
    storeys_count: getStoreysCountFromStacks(
      getStoreyStacksFromDesignVariables(designVariables),
    ),
    building_footprint,
    building_width,
    building_height: storeySum.total_height,
    building_perimeter: designVariables.perimeter,
    building_position: designVariables.location,
    gfa,
    [NAME.HEATED_AREA]: atemp,
    gfa_elevator_per_stairwell_per_level,
    piling_count,
    activity_electricity,
    getStorey,
    getActivity: (type: string) => {
      const variables = {
        apartments: {
          living_area_per_apartment: apartments_living_area_per_apartment,
          balcony_area_per_apartment: apartments_balcony_area_per_apartment,
          bathroom_area_per_apartment: apartments_bathroom_area_per_apartment,
        },
        stairwells: {
          apartments_per_stairwell_per_storey:
            stairwells_apartments_per_stairwell_per_storey,
          elevators_per_stairwell: stairwells_elevators_per_stairwell,
        },
        laundry_room: { gfa: laundry_room_gfa },
        bike_room: { gfa: bike_room_gfa },
        storage: { gfa: storage_gfa },
        building_utilities: { gfa: building_utilities_gfa },
        garage: { gfa: garage_gfa },
      } as const;

      return (variables as Record<string, Record<string, number>>)[type] ?? {};
    },
    isActivity: createIsActivityFn(largestActivityType),
  };
};

/**
 * Same surface as {@link getProjectExpressionVariables}, derived from design variables.
 * Keeps project {@link getStorey} / storey-sum fields; design {@link StoreySummaryVariables}
 * are omitted so they cannot overwrite {@link pickVersionStoreySumValues}.
 */
export const designToProjectExpressionVariablesMigration = (
  designVariables: DesignExpressionVariables,
): DesignToProjectExpressionVariablesMigrationResult => {
  const projectVariables =
    getProjectExpressionVariablesFromDesignExpressionVariables(designVariables);

  const {
    getStorey: _getStorey,
    isActivity: _isActivity,
    ...designWithoutLegacyFunctions
  } = designVariables;

  // Design StoreySummaryVariables (gfa/inner_height/perimeter plus zeros) must not
  // overwrite pickVersionStoreySumValues on project scope.
  const designOverlay = omit(
    designWithoutLegacyFunctions,
    ...getKeys(emptyStoreySummaryVariables),
  );

  return {
    ...projectVariables,
    ...designOverlay,
    getStorey: projectVariables.getStorey,
    isActivity: projectVariables.isActivity,
    getActivity: projectVariables.getActivity,
    building_position: projectVariables.building_position,
  };
};
